import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ChatBubble from './components/ChatBubble';
import ChatHeader from './components/ChatHeader';
import MessageThread from './components/MessageThread';
import MessageInput from './components/MessageInput';
import TicketForm from './components/TicketForm';
import TicketList from './components/TicketList';
import ConversationHistory from './components/ConversationHistory';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const RealTimeCustomerSupport = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('chat'); // 'chat', 'tickets', 'history'
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(2);
  const [isConnected, setIsConnected] = useState(true);

  // Mock current user
  const currentUser = {
    id: 'user_1',
    name: 'John Doe',
    email: 'john@example.com',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
  };

  // Mock agent data
  const supportAgent = {
    id: 'agent_1',
    name: 'Priya Sharma',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9c5e8d4?w=150&h=150&fit=crop&crop=face',
    isOnline: true,
    lastSeen: '2 minutes ago'
  };

  // Mock messages
  const mockMessages = [
    {
      id: 'msg_1',
      senderId: 'agent_1',
      senderName: 'Priya Sharma',
      senderAvatar: 'https://images.unsplash.com/photo-1494790108755-2616b9c5e8d4?w=150&h=150&fit=crop&crop=face',
      type: 'text',
      content: `Hello! I'm Priya from Dru Travels support team. How can I assist you with your travel plans today?`,timestamp: new Date(Date.now() - 1800000),status: 'read'
    },
    {
      id: 'msg_2',senderId: 'user_1',senderName: 'John Doe',senderAvatar: currentUser.avatar,type: 'text',content: 'Hi! I need help with my upcoming trip to Kerala. I want to modify my itinerary.',timestamp: new Date(Date.now() - 1500000),status: 'read'
    },
    {
      id: 'msg_3',senderId: 'agent_1',senderName: 'Priya Sharma',senderAvatar: 'https://images.unsplash.com/photo-1494790108755-2616b9c5e8d4?w=150&h=150&fit=crop&crop=face',type: 'text',content: `I'd be happy to help you modify your Kerala itinerary! Could you please share your booking reference number so I can pull up your details?`,
      timestamp: new Date(Date.now() - 1200000),
      status: 'read'
    },
    {
      id: 'msg_4',
      senderId: 'user_1',
      senderName: 'John Doe',
      senderAvatar: currentUser.avatar,
      type: 'text',
      content: 'Sure! My booking reference is DT2024-KL-001234',
      timestamp: new Date(Date.now() - 900000),
      status: 'read'
    },
    {
      id: 'msg_5',
      senderId: 'agent_1',
      senderName: 'Priya Sharma',
      senderAvatar: 'https://images.unsplash.com/photo-1494790108755-2616b9c5e8d4?w=150&h=150&fit=crop&crop=face',
      type: 'text',
      content: `Perfect! I can see your 7-day Kerala backwaters and hill station package. What specific changes would you like to make to your itinerary?`,
      timestamp: new Date(Date.now() - 600000),
      status: 'delivered'
    }
  ];

  // Mock tickets
  const mockTickets = [
    {
      id: 'TKT001',
      subject: 'Payment not processed for Goa trip',
      category: 'payment',
      priority: 'high',
      status: 'open',
      description: `I made a payment of ₹25,000 for my Goa trip package (booking ref: DT2024-GA-005678) yesterday, but the payment status still shows as pending. The amount has been debited from my account.`,
      createdAt: new Date(Date.now() - 86400000),
      lastReply: new Date(Date.now() - 3600000),
      hasUnreadReplies: true
    },
    {
      id: 'TKT002',
      subject: 'Need vegetarian meal options for Rajasthan tour',
      category: 'travel',
      priority: 'medium',
      status: 'in_progress',
      description: `I have booked a 10-day Rajasthan heritage tour and need to ensure all meals are vegetarian. Can you please coordinate with the hotels and restaurants?`,
      createdAt: new Date(Date.now() - 172800000),
      lastReply: new Date(Date.now() - 7200000),
      hasUnreadReplies: false
    },
    {
      id: 'TKT003',
      subject: 'Travel insurance claim assistance',
      category: 'other',
      priority: 'low',
      status: 'resolved',
      description: `I need help with filing a travel insurance claim for my cancelled Himachal Pradesh trip due to medical emergency.`,
      createdAt: new Date(Date.now() - 259200000),
      lastReply: new Date(Date.now() - 86400000),
      hasUnreadReplies: false
    }
  ];

  // Mock conversation history
  const mockConversations = [
    {
      id: 'conv_1',
      agentName: 'Priya Sharma',
      lastMessage: 'Perfect! I can see your 7-day Kerala backwaters package...',
      lastMessageTime: new Date(Date.now() - 600000),
      status: 'active',
      isOnline: true,
      unreadCount: 1
    },
    {
      id: 'conv_2',
      agentName: 'Rajesh Kumar',
      lastMessage: 'Your refund has been processed successfully.',
      lastMessageTime: new Date(Date.now() - 86400000),
      status: 'resolved',
      isOnline: false,
      unreadCount: 0
    },
    {
      id: 'conv_3',
      agentName: 'Anita Patel',
      lastMessage: 'Thank you for choosing Dru Travels!',
      lastMessageTime: new Date(Date.now() - 172800000),
      status: 'resolved',
      isOnline: true,
      unreadCount: 0
    }
  ];

  useEffect(() => {
    setMessages(mockMessages);
    
    // Simulate WebSocket connection
    const interval = setInterval(() => {
      if (Math.random() > 0.8) {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          // Simulate new message
          if (Math.random() > 0.5) {
            const newMessage = {
              id: `msg_${Date.now()}`,
              senderId: 'agent_1',
              senderName: 'Priya Sharma',
              senderAvatar: supportAgent.avatar,
              type: 'text',
              content: 'Is there anything else I can help you with?',
              timestamp: new Date(),
              status: 'delivered'
            };
            setMessages(prev => [...prev, newMessage]);
            if (!isChatOpen) {
              setUnreadCount(prev => prev + 1);
            }
          }
        }, 2000);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [isChatOpen]);

  const handleSendMessage = (messageData) => {
    const newMessage = {
      id: `msg_${Date.now()}`,
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderAvatar: currentUser.avatar,
      ...messageData,
      timestamp: new Date(),
      status: 'sent'
    };

    setMessages(prev => [...prev, newMessage]);

    // Simulate agent response
    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const agentResponse = {
          id: `msg_${Date.now() + 1}`,
          senderId: 'agent_1',
          senderName: 'Priya Sharma',
          senderAvatar: supportAgent.avatar,
          type: 'text',
          content: 'Thank you for that information. Let me check that for you right away.',
          timestamp: new Date(),
          status: 'delivered'
        };
        setMessages(prev => [...prev, agentResponse]);
      }, 1500);
    }, 1000);
  };

  const handleFileUpload = (file) => {
    const newMessage = {
      id: `msg_${Date.now()}`,
      senderId: currentUser.id,
      senderName: currentUser.name,
      senderAvatar: currentUser.avatar,
      type: 'file',
      fileName: file.name,
      fileSize: file.size,
      timestamp: new Date(),
      status: 'sent'
    };

    setMessages(prev => [...prev, newMessage]);
  };

  const handleTicketSubmit = async (ticketData) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('Ticket submitted:', ticketData);
    setShowTicketForm(false);
    setActiveView('tickets');
  };

  const handleChatToggle = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setUnreadCount(0);
      setIsMinimized(false);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsChatOpen(false);
  };

  const renderMobileView = () => {
    if (isChatOpen) {
      return (
        <div className="fixed inset-0 bg-background z-40 flex flex-col">
          <ChatHeader
            agent={supportAgent}
            onClose={() => setIsChatOpen(false)}
            onMinimize={handleMinimize}
          />
          <MessageThread
            messages={messages}
            currentUser={currentUser}
            isTyping={isTyping}
          />
          <MessageInput
            onSendMessage={handleSendMessage}
            onFileUpload={handleFileUpload}
            disabled={!isConnected}
          />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="p-4 space-y-6">
          {/* View Toggle */}
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setActiveView('chat')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeView === 'chat' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground'
              }`}
            >
              Live Chat
            </button>
            <button
              onClick={() => setActiveView('tickets')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeView === 'tickets' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground'
              }`}
            >
              Tickets
            </button>
            <button
              onClick={() => setActiveView('history')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeView === 'history' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground'
              }`}
            >
              History
            </button>
          </div>

          {/* Content */}
          {activeView === 'chat' && (
            <div className="bg-card rounded-lg border border-border p-6 text-center">
              <Icon name="MessageCircle" size={48} className="text-primary mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Start Live Chat
              </h2>
              <p className="text-muted-foreground mb-6">
                Get instant help from our travel experts. Average response time: 2 minutes.
              </p>
              <Button
                variant="default"
                onClick={() => setIsChatOpen(true)}
                iconName="MessageCircle"
                iconPosition="left"
              >
                Start Chat
              </Button>
            </div>
          )}

          {activeView === 'tickets' && (
            <>
              {showTicketForm ? (
                <TicketForm
                  onSubmit={handleTicketSubmit}
                  onCancel={() => setShowTicketForm(false)}
                />
              ) : (
                <TicketList
                  tickets={mockTickets}
                  onTicketSelect={(ticket) => console.log('Selected ticket:', ticket)}
                  onCreateTicket={() => setShowTicketForm(true)}
                />
              )}
            </>
          )}

          {activeView === 'history' && (
            <ConversationHistory
              conversations={mockConversations}
              onConversationSelect={(conv) => {
                setActiveView('chat');
                setIsChatOpen(true);
              }}
              currentConversationId="conv_1"
            />
          )}
        </div>

        <ChatBubble
          isOpen={isChatOpen}
          onToggle={handleChatToggle}
          unreadCount={unreadCount}
        />
      </div>
    );
  };

  const renderDesktopView = () => {
    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto p-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Customer Support</h1>
            <p className="text-muted-foreground">
              Get instant help with your travel bookings and questions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Sidebar - History & Tickets */}
            <div className="space-y-6">
              <ConversationHistory
                conversations={mockConversations}
                onConversationSelect={(conv) => console.log('Selected conversation:', conv)}
                currentConversationId="conv_1"
              />
            </div>

            {/* Main Chat Area */}
            <div className="lg:col-span-2">
              {showTicketForm ? (
                <TicketForm
                  onSubmit={handleTicketSubmit}
                  onCancel={() => setShowTicketForm(false)}
                />
              ) : (
                <div className="bg-card rounded-lg border border-border h-[600px] flex flex-col">
                  <ChatHeader
                    agent={supportAgent}
                    onClose={() => navigate('/user-dashboard')}
                    showMinimize={false}
                  />
                  <MessageThread
                    messages={messages}
                    currentUser={currentUser}
                    isTyping={isTyping}
                  />
                  <MessageInput
                    onSendMessage={handleSendMessage}
                    onFileUpload={handleFileUpload}
                    disabled={!isConnected}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              onClick={() => setShowTicketForm(true)}
              iconName="Ticket"
              iconPosition="left"
              className="h-12"
            >
              Create Support Ticket
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/user-dashboard')}
              iconName="LayoutDashboard"
              iconPosition="left"
              className="h-12"
            >
              View My Bookings
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('tel:+911234567890')}
              iconName="Phone"
              iconPosition="left"
              className="h-12"
            >
              Call Support
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="md:hidden">
        {renderMobileView()}
      </div>
      <div className="hidden md:block">
        {renderDesktopView()}
      </div>
    </>
  );
};

export default RealTimeCustomerSupport;